import { SingaporeTime } from '../utils/unified-date';

/**
 * Base error class for the application
 * All custom errors should extend this class
 */
export class BaseError extends Error {
  public readonly name: string;
  public readonly timestamp: string;
  public readonly isOperational: boolean;
  public readonly context?: Record<string, unknown>;
  public readonly cause?: Error;

  constructor(
    message: string,
    options: {
      name?: string;
      isOperational?: boolean;
      cause?: Error;
      context?: Record<string, unknown>;
    } = {}
  ) {
    super(message);

    this.name = options.name || this.constructor.name;
    this.timestamp = SingaporeTime.now();
    this.isOperational = options.isOperational !== undefined ? options.isOperational : true;
    this.context = options.context;
    this.cause = options.cause;

    // Maintains proper stack trace for where error was thrown (only in V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Standardized JSON representation of the error
   */
  public toJSON(): Record<string, unknown> {
    const result: Record<string, unknown> = {
      name: this.name,
      message: this.message,
      isOperational: this.isOperational,
      timestamp: this.timestamp,
      context: this.context || {},
    };

    if (this.cause) {
      result.cause = this.formatCause(this.cause);
    }

    return result;
  }

  /**
   * Format error cause for consistent display
   */
  private formatCause(cause: unknown): Record<string, unknown> {
    if (cause instanceof BaseError) {
      return cause.toJSON();
    }

    if (cause instanceof Error) {
      return {
        name: cause.name,
        message: cause.message,
        stack: cause.stack,
      };
    }

    return { unknown: String(cause) };
  }
}
